import { Link } from 'react-router-dom';
import type { PartnerShopListItem } from '@/types/partnerShop';

interface CategoryPartnerShopsProps {
  title: string;
  description: string;
  listPath: string;
  shops: PartnerShopListItem[];
  loading?: boolean;
  emptyMessage: string;
}

function getDisplayName(shop: PartnerShopListItem): string {
  return shop.displayName || shop.shopName;
}

function getDisplayImage(shop: PartnerShopListItem): string {
  return shop.heroThumbnailUrl || shop.heroImageUrl || '';
}

function getPhotoCount(shop: PartnerShopListItem): number {
  return Number(shop.reviewPhotoCount || 0) + Number(shop.lookbookPhotoCount || 0);
}

function PartnerShopCard({ shop }: { shop: PartnerShopListItem }) {
  const displayName = getDisplayName(shop);
  const displayImage = getDisplayImage(shop);
  const photoCount = getPhotoCount(shop);

  return (
    <Link
      to={`/detail/${shop.shopSlug}`}
      className="block overflow-hidden rounded-2xl border border-[rgba(112,115,124,0.22)] bg-white text-[#17171A] transition-[border-color,box-shadow] duration-120 hover:border-[rgba(112,115,124,0.40)] hover:text-[#17171A] hover:shadow-[0_2px_8px_rgba(23,23,23,0.08),0_8px_24px_rgba(23,23,23,0.10)]"
    >
      <div className="relative aspect-[16/10] overflow-hidden bg-[#F4F4F5]">
        {displayImage ? (
          <div
            role="img"
            aria-label={displayName}
            className="h-full w-full bg-cover bg-center bg-no-repeat"
            style={{ backgroundImage: `url("${displayImage}")` }}
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-[#F4F4F5]">
            <i className="ri-image-line text-3xl text-[#B0B8C1]" />
          </div>
        )}
        <span className="absolute left-3 top-3 inline-flex items-center gap-1.5 rounded-full bg-[rgba(23,23,26,0.85)] px-2.5 py-1 text-[11px] font-semibold text-white">
          <span className="h-[5px] w-[5px] rounded-full bg-[#D6927A]" />
          다이렉트 인증
        </span>
      </div>

      <div className="p-5">
        <div className="mb-1 flex items-baseline gap-2">
          <h3 className="m-0 text-[18px] font-bold tracking-[-0.012em] text-[#17171A]">
            {displayName}
          </h3>
          {shop.areaName && (
            <span className="text-[13px] text-[rgba(46,47,51,0.61)]">{shop.areaName}</span>
          )}
        </div>

        <p className="m-0 mb-3.5 text-[13px] text-[rgba(46,47,51,0.61)]">
          후기 {Number(shop.reviewCount || 0).toLocaleString()}건 · 사진 {photoCount.toLocaleString()}장
        </p>

        {shop.reviewSummaryText && (
          <div className="rounded-xl bg-[#F7F7F8] px-3.5 py-3">
            <p className="m-0 line-clamp-2 text-[13.5px] leading-[1.6] text-[#17171A]">
              &quot;{shop.reviewSummaryText}&quot;
            </p>
          </div>
        )}
      </div>
    </Link>
  );
}

export default function CategoryPartnerShops({
  title,
  description,
  listPath,
  shops,
  loading = false,
  emptyMessage,
}: CategoryPartnerShopsProps) {
  return (
    <section className="mx-auto max-w-[1200px] px-5 py-11 md:px-6 md:py-14 lg:py-16">
      <div className="mb-7 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div>
          <h2 className="m-0 mb-2 text-[26px] font-bold tracking-[-0.024em] text-[#17171A] md:text-[30px]">
            {title}
          </h2>
          <p className="m-0 text-[15px] leading-[1.6] text-[rgba(46,47,51,0.61)]">
            {description}
          </p>
        </div>
        <Link
          to={listPath}
          className="inline-flex items-center gap-1 self-start text-[14px] font-semibold text-[#17171A] hover:text-[#A84F38] md:self-auto"
        >
          전체 보기
          <i className="ri-arrow-right-s-line text-[15px]" />
        </Link>
      </div>

      {loading ? (
        <div className="-mx-5 overflow-x-auto px-5 scrollbar-hide md:mx-0 md:px-0">
          <div className="grid min-w-[840px] grid-cols-3 gap-5 md:min-w-0 md:gap-6">
            {[1, 2, 3].map((value) => (
              <div key={value} className="overflow-hidden rounded-2xl border border-[rgba(112,115,124,0.22)] bg-white">
                <div className="aspect-[16/10] animate-pulse bg-[#F4F4F5]" />
                <div className="p-5">
                  <div className="mb-2 h-5 w-1/2 animate-pulse rounded bg-[#F4F4F5]" />
                  <div className="mb-3.5 h-3 w-2/3 animate-pulse rounded bg-[#F4F4F5]" />
                  <div className="rounded-xl bg-[#F7F7F8] px-3.5 py-3">
                    <div className="mb-2 h-3 w-full animate-pulse rounded bg-[#EAEAEC]" />
                    <div className="h-3 w-4/5 animate-pulse rounded bg-[#EAEAEC]" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : shops.length > 0 ? (
        <div className="-mx-5 overflow-x-auto px-5 scrollbar-hide md:mx-0 md:px-0">
          <div className="grid min-w-[840px] grid-cols-3 gap-5 md:min-w-0 md:gap-6">
            {shops.slice(0, 3).map((shop) => (
              <PartnerShopCard key={shop.id} shop={shop} />
            ))}
          </div>
        </div>
      ) : (
        <div className="rounded-2xl border border-[rgba(112,115,124,0.22)] bg-white px-5 py-12 text-center">
          <p className="m-0 text-[15px] text-[rgba(46,47,51,0.61)]">{emptyMessage}</p>
        </div>
      )}
    </section>
  );
}
